import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

export type LoginErrorCode = 'unauthorized' | 'bad_request' | 'network' | 'unknown';

export interface LoginSuccess {
    ok: true;
}
export interface LoginFailure {
    ok: false;
    error: LoginErrorCode;
}
export type LoginResult = LoginSuccess | LoginFailure;

interface LoginRedeemResponse {
    jwt: string;
    /** Unix epoch seconds */
    exp: number;
}

const STORAGE_KEY = 'ctep:auth:jwt';

/**
 * Holds the inspector's JWT for the session and exposes signal-based auth state.
 *
 * Storage: sessionStorage so the token clears on tab close. Switch to localStorage
 * if cross-tab persistence is wanted later.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
    readonly #http = inject(HttpClient);
    readonly #router = inject(Router);

    readonly #jwt = signal<string | null>(this.#readStoredJwt());
    readonly jwt = this.#jwt.asReadonly();
    readonly isAuthenticated = computed(() => this.#jwt() !== null);

    async login(password: string): Promise<LoginResult> {
        try {
            const response = await firstValueFrom(
                this.#http.post<LoginRedeemResponse>('/api/auth/login-redeem', { password })
            );
            this.#storeJwt(response.jwt);
            return { ok: true };
        } catch (err) {
            return { ok: false, error: this.#errorCodeFor(err) };
        }
    }

    logout(): void {
        this.#clearJwt();
        this.#router.navigate(['/login']);
    }

    /** Called by the auth interceptor on 401 responses to wipe expired/invalid tokens. */
    clearSession(): void {
        this.#clearJwt();
    }

    #storeJwt(jwt: string): void {
        sessionStorage.setItem(STORAGE_KEY, jwt);
        this.#jwt.set(jwt);
    }

    #clearJwt(): void {
        sessionStorage.removeItem(STORAGE_KEY);
        this.#jwt.set(null);
    }

    #readStoredJwt(): string | null {
        if (typeof sessionStorage === 'undefined') return null;
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        if (this.#isExpired(raw)) {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return raw;
    }

    /**
     * Decode the JWT payload (no signature check — that's the server's job).
     * We only need the `exp` claim to avoid sending obviously dead tokens.
     */
    #isExpired(jwt: string): boolean {
        try {
            const payloadSegment = jwt.split('.')[1];
            if (!payloadSegment) return true;
            const payload = JSON.parse(this.#decodeBase64Url(payloadSegment)) as { exp?: number };
            if (typeof payload.exp !== 'number') return true;
            return payload.exp * 1000 <= Date.now();
        } catch {
            return true;
        }
    }

    /**
     * JWT segments are base64url-encoded (RFC 7515): `-`/`_` instead of `+`/`/`
     * and no `=` padding. `atob` only accepts standard base64, so translate first.
     */
    #decodeBase64Url(segment: string): string {
        const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        return atob(padded);
    }

    #errorCodeFor(err: unknown): LoginErrorCode {
        if (err instanceof HttpErrorResponse) {
            if (err.status === 401) return 'unauthorized';
            if (err.status === 400) return 'bad_request';
            if (err.status === 0) return 'network';
        }
        return 'unknown';
    }
}
